import React, { useEffect, useState, useRef, type FC } from 'react';
import { type WatchQueryFetchPolicy } from '@apollo/client';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Text, Inline } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

import { useAccessRequests } from './useAccessRequests';
import { NewAccessRequestIndicator } from './NewAccessRequestIndicator';

const i18n = defineMessages({
	buttonTitle: {
		id: 'access-requests.button-title',
		defaultMessage: 'Requests',
		description: 'The text of the button which opens the Access Requests modal',
	},
});

export interface AccessRequestsButtonProps {
	tryCacheFirst?: boolean;
	/**
	 * Needed to ensure USD (or any other parent context)
	 * has fully loaded before showing the button
	 * */
	delayShowing?: boolean;

	/**
	 * Instead of exporting ref-s to the outside world, we can just declaratively tell the
	 * button to focus itself. Makes for an easier read in the parent code.
	 */
	shouldAutoFocus?: boolean;

	/** Used for analytics purposes. */
	source: string;

	/** Now what would that be? */
	onClick: () => void;

	/** Once the button is auto-focused, we might want to do something */
	onAutoFocus?: () => void;

	onDataLoaded?: (isVisible: boolean) => void;
}

export const AccessRequestsButton: FC<AccessRequestsButtonProps> = ({
	tryCacheFirst = false,
	delayShowing = false,
	shouldAutoFocus,
	source,
	onClick,
	onAutoFocus,
	onDataLoaded,
}) => {
	const [fetchPolicy] = useState<WatchQueryFetchPolicy>(
		tryCacheFirst ? 'cache-and-network' : 'network-only',
	);

	const buttonRef = useRef<HTMLButtonElement>(null);

	const { requests, error, hasFullyLoaded, contentId } = useAccessRequests(
		fetchPolicy,
		'accessRequestsButton',
	);

	const count = requests?.length ?? 0;
	const showButton = count > 0 && !delayShowing && !error;

	useEffect(() => {
		if (shouldAutoFocus) {
			buttonRef.current?.focus();
			onAutoFocus?.();
		}
	}, [shouldAutoFocus, onAutoFocus]);

	useEffect(() => {
		if (hasFullyLoaded) {
			onDataLoaded?.(showButton);
		}
	}, [hasFullyLoaded, onDataLoaded, showButton]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (showButton) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'viewed',
					actionSubject: 'accessRequestsButton',
					source,
					attributes: {
						count: count > 10 ? '10+' : count,
						contentId,
					},
				},
			}).fire();
		}
	}, [showButton, createAnalyticsEvent, source, count, contentId]);

	if (!showButton) {
		return null;
	}

	// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
	const appearance = UNSAFE_expValEqualsNoExposure(
		'cc_fe_realtime_access_requests',
		'isEnabled',
		true,
	)
		? 'default'
		: 'important';

	return (
		<NewAccessRequestIndicator top={3} right={7} isInsideOverlay>
			<Button appearance="subtle" onClick={onClick} ref={buttonRef}>
				<Inline space="space.075" alignBlock="center">
					<Text>
						<FormattedMessage {...i18n.buttonTitle} />
					</Text>
					<Badge appearance={appearance} max={9}>
						{count}
					</Badge>
				</Inline>
			</Button>
		</NewAccessRequestIndicator>
	);
};
